import { startRegistration } from '@simplewebauthn/browser'
import type { PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/browser'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Button, Flex, Form, Input, Modal, Typography } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  accountPasskeysListOptions,
  accountPasskeysRegisterStartCreate,
  accountPasskeysRegisterVerifyCreate
} from 'src/api/main'
import { useInlineApiErrors } from 'src/utils'

import { FingerprintIcon } from './SocialProviderIcons'

const { Paragraph } = Typography

const DISMISSED_KEY = 'passkey_prompt_dismissed'

async function isDevicePasskeyCapable(): Promise<boolean> {
  if (typeof window === 'undefined' || !window.PublicKeyCredential) {
    return false
  }
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
  } catch {
    return false
  }
}

export function getDeviceDefaultName(): string {
  const ua = navigator.userAgent

  let browser = 'Browser'
  if (ua.includes('Edg/')) browser = 'Edge'
  else if (ua.includes('Chrome/') && !ua.includes('Edg/')) browser = 'Chrome'
  else if (ua.includes('Firefox/')) browser = 'Firefox'
  else if (ua.includes('Safari/') && !ua.includes('Chrome/')) browser = 'Safari'

  let os = ''
  if (ua.includes('Windows')) os = 'Windows'
  else if (ua.includes('Macintosh') || ua.includes('Mac OS')) os = 'Mac'
  else if (ua.includes('iPhone')) os = 'iPhone'
  else if (ua.includes('iPad')) os = 'iPad'
  else if (ua.includes('Android')) os = 'Android'
  else if (ua.includes('Linux')) os = 'Linux'
  else if (ua.includes('CrOS')) os = 'ChromeOS'

  return os ? `${browser} on ${os}` : browser
}

type Props = {
  open: boolean
  onComplete: () => void
  onSkip: () => void
}

export const PasskeyRecommendationModal = ({
  open,
  onComplete,
  onSkip
}: Props) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const { showApiErrors, errorAlert } = useInlineApiErrors()
  const [form] = Form.useForm<{ name: string }>()
  const [deviceCapable, setDeviceCapable] = useState<boolean | null>(null)
  const [shouldShow] = useState(true)

  // Check device capability
  useEffect(() => {
    if (!open) return
    isDevicePasskeyCapable().then((capable) => {
      setDeviceCapable(capable)
      if (!capable) onSkip()
    })
  }, [open, onSkip])

  // Check if user already has passkeys
  const passkeysQuery = useQuery({
    ...accountPasskeysListOptions(),
    enabled: open && deviceCapable === true
  })

  useEffect(() => {
    if (!open || deviceCapable !== true) return
    if (passkeysQuery.data && (passkeysQuery.data.results?.length ?? 0) > 0) {
      onSkip()
    }
  }, [open, deviceCapable, passkeysQuery.data, onSkip])

  const { mutateAsync: registerPasskey, isPending } = useMutation({
    mutationFn: async (name: string) => {
      const { data: options } = await accountPasskeysRegisterStartCreate({
        body: { name }
      })
      const credential = await startRegistration({
        optionsJSON:
          options as unknown as PublicKeyCredentialCreationOptionsJSON
      })
      const { data: passkey } = await accountPasskeysRegisterVerifyCreate({
        body: {
          credential: credential as unknown as Record<string, unknown>,
          name
        }
      })

      return passkey
    },
    onSuccess: () => {
      message.success(t('auth.passkeyRecommendation.success'))
      onComplete()
    },
    onError: (e: Error) => {
      if (e.name === 'NotAllowedError') return
      if (e.name === 'InvalidStateError') {
        message.info(t('auth.passkeyRecommendation.alreadyRegistered'))
        onComplete()
        return
      }
      showApiErrors(e, form)
    }
  })

  const handleSetup = () => {
    form.validateFields().then((values) => {
      registerPasskey(values.name)
    })
  }

  const handleDontAsk = () => {
    localStorage.setItem(DISMISSED_KEY, '1')
    onSkip()
  }

  // Don't render until we know the device is capable and user has no passkeys
  const isReady =
    deviceCapable === true &&
    passkeysQuery.isFetched &&
    (passkeysQuery.data?.results?.length ?? 0) === 0

  if (!shouldShow || !isReady) return null

  return (
    <Modal
      open={open}
      title={
        <Flex align="center" gap={8}>
          <FingerprintIcon size={20} />
          {t('auth.passkeyRecommendation.title')}
        </Flex>
      }
      footer={null}
      onCancel={onSkip}
      centered
      destroyOnHidden
    >
      <Flex orientation="vertical" gap={20} style={{ paddingTop: 8 }}>
        <Paragraph type="secondary" style={{ margin: 0 }}>
          {t('auth.passkeyRecommendation.description')}
        </Paragraph>

        {errorAlert}

        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          initialValues={{ name: getDeviceDefaultName() }}
        >
          <Form.Item
            name="name"
            label={t('auth.passkeyRecommendation.nameLabel')}
            rules={[{ required: true, message: t('required_field_error') }]}
          >
            <Input
              placeholder={t('auth.passkeyRecommendation.namePlaceholder')}
            />
          </Form.Item>
        </Form>

        <Flex orientation="vertical" gap={12}>
          <Button
            type="primary"
            size="large"
            icon={<FingerprintIcon size={18} />}
            loading={isPending}
            onClick={handleSetup}
            block
          >
            {t('auth.passkeyRecommendation.setup')}
          </Button>
          <Flex justify="space-between" align="center">
            <Button type="link" size="small" onClick={onSkip}>
              {t('auth.passkeyRecommendation.later')}
            </Button>
            <Button type="link" size="small" onClick={handleDontAsk}>
              {t('auth.passkeyRecommendation.dontAsk')}
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Modal>
  )
}

/** Check if the passkey prompt should be shown (not previously dismissed). */
export const isPasskeyPromptDismissed = (): boolean =>
  localStorage.getItem(DISMISSED_KEY) === '1'

const PENDING_KEY = 'passkey_prompt_pending'

/** Call after any successful login to schedule the passkey prompt. */
export const schedulePasskeyPrompt = (): void => {
  if (!isPasskeyPromptDismissed()) {
    sessionStorage.setItem(PENDING_KEY, '1')
  }
}

/** Consume the pending flag (returns true once, then clears). */
export const consumePasskeyPrompt = (): boolean => {
  if (sessionStorage.getItem(PENDING_KEY)) {
    sessionStorage.removeItem(PENDING_KEY)
    return true
  }
  return false
}
